const toTime = (date: string): number => new Date(date).getTime();

/**
 * The number of milliseconds two periods overlap (0 if they don't touch).
 * Periods are inclusive date ranges expressed as ISO `YYYY-MM-DD` strings.
 */
const overlapMs = (a: Period, b: Period): number => {
  const start = Math.max(toTime(a.startDate), toTime(b.startDate));
  const end = Math.min(toTime(a.endDate), toTime(b.endDate));
  return Math.max(0, end - start);
};

/**
 * Given a `target` period from one bonus program, pick the `candidate` period from another program
 * that best lines up with it in time.
 *
 * The driver entity screen shows one tab per bonus program, but the URL carries a single
 * `selectedBonusPeriodId` (stamped on drill-down). Programs can run on different cadences — e.g.
 * Halvor's Productivity bonus is 4-weekly while Safety/Fuel are quarterly — so the id resolves only
 * for the program it came from. To keep the other tabs on a sensible period we map the source
 * period onto each other program by greatest date-range overlap.
 *
 * "Most overlap" (rather than "contains the source start date") is deliberate: a source period can
 * straddle a boundary the candidate cadence doesn't share (Halvor's Dec 29 – Jan 25 period starts
 * in the previous calendar year, before any Jan 1-anchored quarter), so start-containment would
 * miss. Ties resolve to the first candidate, which — with chronologically-ordered periods — is the
 * earliest. Returns undefined when nothing overlaps, so callers fall back to the program default.
 */
const getOverlappingBonusPeriod = ({
  target,
  candidates,
}: {
  target: Period;
  candidates: BonusTypes.BonusPeriod[];
}): BonusTypes.BonusPeriod | undefined => {
  let best: BonusTypes.BonusPeriod | undefined;
  let bestOverlap = 0;

  candidates.forEach((candidate) => {
    const overlap = overlapMs(target, candidate);
    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      best = candidate;
    }
  });

  return best;
};

export default getOverlappingBonusPeriod;
